/**
 * useAgGridColumns - Hook para definir las columnas de la tabla AG Grid de Invoices/Orders
 *
 * Define todas las columnas disponibles para la tabla de shipments elegibles para facturación,
 * incluyendo columnas sortables (id, ref, order_created_at, processed_at, date_tracking_update, last_status_at),
 * renderizado customizado de celdas, y acciones del dropdown (editar, bloquear, prefacturar).
 *
 * @returns {Object} Objeto con columnas de AG Grid y TanStack Table
 */

import { formatCurrencyAmount, useCurrency } from '@modules/Orders/shared/hooks/useCurrency';
import { CustomColDef } from '@shared/components/ui/AgGridTable';
import { ColumnDef } from '@tanstack/react-table';
import { ColDef } from 'ag-grid-community';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { BlockedShipmentDropdownActions } from '../components/interaction/BlockedShipmentDropdownActions';
import { ShipmentDropdownActions } from '../components/interaction/ShipmentDropdownActions';
import { useInvoicesPermissions } from './useInvoicesPermissions';

// 🎯 Función utilitaria para formatear fechas como dd/mm/yyyy
const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return '-';

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';

    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();

    return `${day}/${month}/${year}`;
  } catch {
    return '-';
  }
};

/** Fecha y hora (p. ej. último estado logístico: "2026-02-26 15:37:00") */
const formatDateTime = (dateString: string | null | undefined): string => {
  if (!dateString) return '-';
  try {
    const normalized = dateString.includes('T') ? dateString : dateString.replace(' ', 'T');
    const date = new Date(normalized);
    if (isNaN(date.getTime())) return '-';
    return new Intl.DateTimeFormat('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  } catch {
    return '-';
  }
};

const convertToTanStackColumns = (agGridColumns: ColDef[], t: (key: string) => string): ColumnDef<any>[] => {
  return agGridColumns
    .filter((col) => {
      const field = col.field || col.headerName || 'unknown';
      return field !== 'actions' && field !== 'unknown';
    })
    .map((col) => {
      let translatedHeader = col.headerName || 'Columna';

      if (translatedHeader.includes('table.')) {
        translatedHeader = t(translatedHeader);
      }

      return {
        id: col.field || col.headerName || 'unknown',
        accessorKey: col.field || col.headerName || 'unknown',
        header: translatedHeader,
      };
    });
};

interface UseAgGridColumnsProps {
  hasTeamData?: boolean;
  selectedColumns?: string[];
  onEditSingleShipment?: (shipmentId: number) => void;
  onBlockSingleShipment?: (shipmentId: number) => void;
  onInvoiceSingleShipment?: (shipmentId: number) => void;
}

export const useAgGridColumns = ({
  selectedColumns = [],
  onEditSingleShipment,
  onBlockSingleShipment,
  onInvoiceSingleShipment,
}: UseAgGridColumnsProps) => {
  const { t } = useTranslation('invoicesOrdersModule');
  const currency = useCurrency(); // 🎯 Obtener moneda del team desde /me
  const { canCreatePreinvoices, canEditLines, canBlockLines } = useInvoicesPermissions();
  const hasShipmentActions = canCreatePreinvoices || canEditLines || canBlockLines;
  console.log('🧪 [EligibleShipments] useAgGridColumns permissions snapshot', {
    canCreatePreinvoices,
    canEditLines,
    canBlockLines,
    hasShipmentActions,
  });

  const allColumnDefs: ColDef[] = useMemo(() => {
    const baseColumns: CustomColDef[] = [
      {
        field: 'ref',
        headerName: t('table.order'),
        sortable: true, // ✅ Sortable: ref
        minWidth: 120,
        valueGetter: (params) => {
          return params.data?.order?.ref ?? '';
        },
      },
      {
        field: 'order_id',
        headerName: t('table.order_id'),
        sortable: false,
        minWidth: 100,
        valueGetter: (params) => {
          return params.data?.order_id ?? params.data?.order?.id ?? '';
        },
      },
      {
        field: 'shop',
        headerName: t('table.shop'),
        sortable: false,
        minWidth: 180,
        valueGetter: (params) => params.data?.order?.shop?.name ?? '',
      },
      {
        field: 'client',
        headerName: t('table.client'),
        sortable: false,
        minWidth: 180,
        valueGetter: (params) => params.data?.order?.team?.name ?? '',
      },
      {
        field: 'payment_method',
        headerName: t('table.payment_method'),
        sortable: false,
        minWidth: 160,
        valueGetter: (params) => (params.data?.order as any)?.payment_method_method ?? '',
      },
      {
        field: 'order_total',
        headerName: t('table.order_total'),
        sortable: false,
        minWidth: 130,
        valueGetter: (params) => (params.data?.order as any)?.total ?? 0,
        cellRenderer: (params: any) => {
          const total = (params.data?.order as any)?.total ?? 0;
          return <span className="text-sm font-medium">{formatCurrencyAmount(total, currency)}</span>;
        },
      },
      {
        field: 'processed_at',
        headerName: t('table.shipment_date'),
        sortable: true, // ✅ Sortable: processed_at
        minWidth: 160,
        valueGetter: (params) => {
          const shippingDate = params.data?.shipping_date;
          if (shippingDate && typeof shippingDate === 'object') {
            return shippingDate.date ?? '';
          }
          return shippingDate ?? '';
        },
        cellRenderer: (params: any) => {
          const shippingDate = params.data?.shipping_date;
          const dateStr = shippingDate?.date || shippingDate || null;
          return <span className="text-sm">{formatDate(dateStr)}</span>;
        },
      },
      {
        field: 'type',
        headerName: t('table.type'),
        sortable: false,
        minWidth: 130,
        valueGetter: (params) => params.data?.type ?? '',
        cellRenderer: (params: any) => {
          const type = params.data?.type;

          // 🎯 Mapeo de tipos a traducciones
          const typeTranslations: Record<string, string> = {
            SHIPPING: t('filters.typeOptions.shipping', 'Envío'),
            RETURN: t('filters.typeOptions.return', 'Devolución'),
            RESHIPMENT: t('filters.typeOptions.reshipment', 'Reenvío'),
          };

          return <span className="text-sm">{typeTranslations[type] || type || '-'}</span>;
        },
      },
      {
        field: 'internal_status',
        headerName: t('table.status'),
        sortable: false,
        minWidth: 140,
        valueGetter: (params) => params.data?.order?.status?.description_code ?? '',
      },
      {
        field: 'logistic_status',
        headerName: t('table.logistic_status'),
        sortable: false,
        minWidth: 140,
        valueGetter: (params) => params.data?.order?.shipment_status?.name ?? t('table.no_logistic_status'),
      },
      {
        field: 'date_tracking_update',
        headerName: t('table.last_logistic_update'),
        sortable: true,
        minWidth: 190,
        valueGetter: (params) => params.data?.date_tracking_update ?? '',
        cellRenderer: (params: any) => (
          <span className="text-sm">{formatDateTime(params.data?.date_tracking_update)}</span>
        ),
      },
      {
        field: 'last_status_at',
        headerName: t('table.last_status_at'),
        sortable: true,
        minWidth: 190,
        valueGetter: (params) => params.data?.last_status_at ?? '',
        cellRenderer: (params: any) => <span className="text-sm">{formatDateTime(params.data?.last_status_at)}</span>,
      },
      {
        field: 'courier',
        headerName: t('table.courier'),
        sortable: false,
        minWidth: 140,
        valueGetter: (params) => params.data?.courier?.name ?? params.data?.order?.shipment?.courier?.name ?? '',
        cellRenderer: (params: any) => {
          const courierName = params.data?.courier?.name ?? params.data?.order?.shipment?.courier?.name ?? '-';
          return <span className="text-sm">{courierName}</span>;
        },
      },
      {
        field: 'country',
        headerName: t('table.country'),
        sortable: false,
        minWidth: 120,
        valueGetter: (params) => params.data?.country ?? '',
      },
      {
        field: 'city',
        headerName: t('table.city'),
        sortable: false,
        minWidth: 140,
        valueGetter: (params) => params.data?.city ?? '',
      },
      {
        field: 'zip',
        headerName: t('table.zip'),
        sortable: false,
        minWidth: 100,
        valueGetter: (params) => params.data?.zip ?? '',
      },
      {
        field: 'num_items',
        headerName: t('table.num_items'),
        sortable: false,
        minWidth: 100,
        valueGetter: (params) => params.data?.num_items ?? 0,
      },
      {
        field: 'weight_internal_kg',
        headerName: t('table.weight_internal_kg'),
        sortable: false,
        minWidth: 100,
        valueGetter: (params) => params.data?.weight_internal_kg ?? 0,
        cellRenderer: (params: any) => {
          const weight = params.data?.weight_internal_kg ?? 0;
          return (
            <span className="text-sm">
              {new Intl.NumberFormat('es-ES', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }).format(weight)}{' '}
              kg
            </span>
          );
        },
      },
      {
        field: 'period',
        headerName: t('table.period'),
        sortable: false,
        minWidth: 120,
        valueGetter: (params) => params.data?.period ?? '',
      },
      {
        field: 'shipping_base',
        headerName: t('table.shipping'),
        sortable: false,
        minWidth: 120,
        valueGetter: (params) => {
          const shippingLine = params.data?.lines?.find((l: any) => l.concept === 'SHIPPING');
          return shippingLine?.base_amount ?? 0;
        },
        cellRenderer: (params: any) => {
          const shippingLine = params.data?.lines?.find((l: any) => l.concept === 'SHIPPING');
          const amount = shippingLine?.base_amount ?? 0;
          return (
            <span className="text-sm">
              {new Intl.NumberFormat('es-ES', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }).format(amount)}{' '}
              {currency}
            </span>
          );
        },
      },
      {
        field: 'preparation_base',
        headerName: t('table.preparation'),
        sortable: false,
        minWidth: 130,
        valueGetter: (params) => {
          const preparationLine = params.data?.lines?.find((l: any) => l.concept === 'PREPARATION');
          return preparationLine?.base_amount ?? 0;
        },
        cellRenderer: (params: any) => {
          const preparationLine = params.data?.lines?.find((l: any) => l.concept === 'PREPARATION');
          const amount = preparationLine?.base_amount ?? 0;
          return (
            <span className="text-sm">
              {new Intl.NumberFormat('es-ES', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }).format(amount)}{' '}
              {currency}
            </span>
          );
        },
      },
      {
        field: 'cod_base',
        headerName: t('table.cod'),
        sortable: false,
        minWidth: 120,
        valueGetter: (params) => {
          const codLine = params.data?.lines?.find((l: any) => l.concept === 'COD');
          return codLine?.base_amount ?? 0;
        },
        cellRenderer: (params: any) => {
          const codLine = params.data?.lines?.find((l: any) => l.concept === 'COD');
          const amount = codLine?.base_amount ?? 0;
          return (
            <span className="text-sm">
              {new Intl.NumberFormat('es-ES', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }).format(amount)}{' '}
              {currency}
            </span>
          );
        },
      },
      {
        field: 'total',
        headerName: t('table.total'),
        sortable: false,
        minWidth: 120,
        valueGetter: (params) => {
          // Calcular el total sumando base_amount de todas las líneas (sin impuestos)
          const lines = params.data?.lines || [];
          const total = lines.reduce((sum: number, line: any) => sum + (line.base_amount || 0), 0);
          return total;
        },
        cellRenderer: (params: any) => {
          const lines = params.data?.lines || [];
          const total = lines.reduce((sum: number, line: any) => sum + (line.base_amount || 0), 0);
          return (
            <span className="text-sm font-medium">
              {new Intl.NumberFormat('es-ES', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }).format(total)}{' '}
              {currency}
            </span>
          );
        },
      },
      {
        field: 'financial_status',
        headerName: t('table.financial_status'),
        sortable: false, // ❌ No sortable según API
        minWidth: 150,
        valueGetter: (params) => params.data?.financial_status ?? '',
        cellRenderer: (params: any) => {
          const status = params.data?.financial_status;

          // 🎯 Mapeo de estados a traducciones
          const statusTranslations: Record<string, string> = {
            NOT_INVOICED: t('filters.financialStatusOptions.notInvoiced', 'No facturado'),
            PREINVOICED: t('filters.financialStatusOptions.preinvoiced', 'Prefacturado'),
            INVOICED: t('filters.financialStatusOptions.invoiced', 'Facturado'),
            BLOCKED: t('filters.financialStatusOptions.blocked', 'Bloqueado'),
          };

          return <span className="text-sm">{statusTranslations[status] || status || '-'}</span>;
        },
      },
      {
        field: 'pre_invoice_id',
        headerName: t('table.pre_invoice_no'),
        sortable: false,
        minWidth: 180,
        valueGetter: (params) => params.data?.pre_invoice?.code ?? params.data?.pre_invoice_id ?? '',
      },
      {
        field: 'invoice_id',
        headerName: t('table.invoice_no'),
        sortable: false,
        minWidth: 180,
        valueGetter: (params) => params.data?.invoice?.erp_number ?? '',
      },
    ];

    if (hasShipmentActions) {
      baseColumns.push({
        field: 'actions',
        headerName: '',
        sortable: false,
        filter: false,
        width: 50,
        maxWidth: 50,
        minWidth: 50,
        pinned: 'right',
        lockPosition: 'right',
        suppressMovable: true,
        suppressSizeToFit: true,
        suppressAutoSize: true,
        cellRenderer: (params: any) => {
          const shipment = params.data;
          if (!shipment || !shipment.id) {
            return null;
          }

          // Maneja la acción seleccionada en el menú contextual
          const handleDropdownAction = (action: string, shipmentId: number) => {
            if (action === 'edit' && onEditSingleShipment) {
              onEditSingleShipment(shipmentId);
            } else if (action === 'block' && onBlockSingleShipment) {
              onBlockSingleShipment(shipmentId);
            } else if (action === 'prefacturar' && onInvoiceSingleShipment) {
              onInvoiceSingleShipment(shipmentId);
            }
          };

          return (
            <div className="flex justify-center w-full">
              <ShipmentDropdownActions shipmentId={shipment.id} onAction={handleDropdownAction} />
            </div>
          );
        },
      });
    }

    return baseColumns;
  }, [t, currency, onEditSingleShipment, onBlockSingleShipment, onInvoiceSingleShipment, hasShipmentActions]);

  const tanStackColumns = useMemo(() => convertToTanStackColumns(allColumnDefs, t), [allColumnDefs, t]);

  const filteredColumnDefs = useMemo(() => {
    if (!selectedColumns || selectedColumns.length === 0) {
      return allColumnDefs;
    }

    const filtered = allColumnDefs.filter((col) => {
      const field = col.field || '';

      if (field === 'actions') {
        return hasShipmentActions;
      }

      return selectedColumns.includes(field);
    });

    console.log('🧪 [EligibleShipments] filteredColumnDefs computed', {
      selectedColumns,
      totalColumns: allColumnDefs.length,
      filteredCount: filtered.length,
      hasShipmentActions,
    });

    return filtered;
  }, [allColumnDefs, selectedColumns, hasShipmentActions]);

  return {
    allColumnDefs: filteredColumnDefs,
    filteredColumnDefs,
    tanStackColumns,
  };
};

interface UseBlockedShipmentsColumnsProps {
  hasTeamData?: boolean;
  selectedColumns?: string[];
  onUnblockShipment?: (blockId: number) => void;
}

/**
 * useBlockedShipmentsColumns - Hook para definir las columnas de la tabla AG Grid de shipments bloqueados
 *
 * Define todas las columnas disponibles para la tabla de shipments bloqueados,
 * incluyendo columnas específicas: razón de bloqueo, comentario y usuario que bloqueó.
 */
export const useBlockedShipmentsColumns = ({
  selectedColumns = [],
  onUnblockShipment,
}: UseBlockedShipmentsColumnsProps) => {
  const { t } = useTranslation('invoicesOrdersModule');
  const currency = useCurrency();
  // Para desbloquear se necesita el permiso shipments.unblock (canRestoreLines)
  const { canRestoreLines } = useInvoicesPermissions();
  const hasShipmentActions = canRestoreLines;

  const allColumnDefs: ColDef[] = useMemo(() => {
    const baseColumns: CustomColDef[] = [
      {
        field: 'ref',
        headerName: t('table.order'),
        sortable: true,
        minWidth: 120,
        valueGetter: (params) => {
          // Para bloqueados, puede venir desde shipment.order.ref o directamente
          return params.data?.shipment?.order?.ref ?? params.data?.order_id ?? '';
        },
      },
      {
        field: 'shop',
        headerName: t('table.shop'),
        sortable: false,
        minWidth: 180,
        valueGetter: (params) => {
          return params.data?.shipment?.order?.shop?.name ?? '';
        },
      },
      {
        field: 'client',
        headerName: t('table.client'),
        sortable: false,
        minWidth: 180,
        valueGetter: (params) => {
          return params.data?.shipment?.order?.team?.name ?? '';
        },
      },
      {
        field: 'payment_method',
        headerName: t('table.payment_method'),
        sortable: false,
        minWidth: 160,
        valueGetter: (params) => {
          return (params.data?.shipment?.order as any)?.payment_method_method ?? '';
        },
      },
      {
        field: 'order_total',
        headerName: t('table.order_total'),
        sortable: false,
        minWidth: 130,
        valueGetter: (params) => {
          return (params.data?.shipment?.order as any)?.total ?? 0;
        },
        cellRenderer: (params: any) => {
          const total = (params.data?.shipment?.order as any)?.total ?? 0;
          return (
            <span className="text-sm font-medium">
              {new Intl.NumberFormat('es-ES', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }).format(total)}{' '}
              {currency}
            </span>
          );
        },
      },
      {
        field: 'processed_at',
        headerName: t('table.shipment_date'),
        sortable: true,
        minWidth: 160,
        valueGetter: (params) => {
          const shippingDate = params.data?.shipment?.shipping_date;
          if (shippingDate && typeof shippingDate === 'object') {
            return shippingDate.date ?? '';
          }
          return shippingDate ?? '';
        },
        cellRenderer: (params: any) => {
          const shippingDate = params.data?.shipment?.shipping_date;
          const dateStr = shippingDate?.date || shippingDate || null;
          return <span className="text-sm">{formatDate(dateStr)}</span>;
        },
      },
      {
        field: 'type',
        headerName: t('table.type'),
        sortable: false,
        minWidth: 130,
        valueGetter: (params) => params.data?.shipment?.type ?? '',
        cellRenderer: (params: any) => {
          const type = params.data?.shipment?.type;
          const typeTranslations: Record<string, string> = {
            SHIPPING: t('filters.typeOptions.shipping', 'Envío'),
            RETURN: t('filters.typeOptions.return', 'Devolución'),
            RESHIPMENT: t('filters.typeOptions.reshipment', 'Reenvío'),
          };
          return <span className="text-sm">{typeTranslations[type] || type || '-'}</span>;
        },
      },
      {
        field: 'internal_status',
        headerName: t('table.status'),
        sortable: false,
        minWidth: 140,
        valueGetter: (params) => params.data?.shipment?.order?.status?.description_code ?? '',
      },
      {
        field: 'logistic_status',
        headerName: t('table.logistic_status'),
        sortable: false,
        minWidth: 140,
        valueGetter: (params) => params.data?.shipment?.order?.shipment_status?.name ?? t('table.no_logistic_status'),
      },
      {
        field: 'courier',
        headerName: t('table.courier'),
        sortable: false,
        minWidth: 140,
        valueGetter: (params) =>
          params.data?.shipment?.courier?.name ?? params.data?.shipment?.order?.shipment?.courier?.name ?? '',
        cellRenderer: (params: any) => {
          const courierName =
            params.data?.shipment?.courier?.name ?? params.data?.shipment?.order?.shipment?.courier?.name ?? '-';
          return <span className="text-sm">{courierName}</span>;
        },
      },
      {
        field: 'country',
        headerName: t('table.country'),
        sortable: false,
        minWidth: 120,
        valueGetter: (params) => params.data?.shipment?.country ?? '',
      },
      {
        field: 'city',
        headerName: t('table.city'),
        sortable: false,
        minWidth: 140,
        valueGetter: (params) => params.data?.shipment?.city ?? '',
      },
      {
        field: 'zip',
        headerName: t('table.zip'),
        sortable: false,
        minWidth: 100,
        valueGetter: (params) => params.data?.shipment?.zip ?? '',
      },
      {
        field: 'num_items',
        headerName: t('table.num_items'),
        sortable: false,
        minWidth: 100,
        valueGetter: (params) => params.data?.shipment?.num_items ?? 0,
      },
      {
        field: 'weight_internal_kg',
        headerName: t('table.weight_internal_kg'),
        sortable: false,
        minWidth: 120,
        valueGetter: (params) => params.data?.shipment?.weight_internal_kg ?? 0,
        cellRenderer: (params: any) => {
          const weight = params.data?.shipment?.weight_internal_kg ?? 0;
          return (
            <span className="text-sm">
              {new Intl.NumberFormat('es-ES', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }).format(weight)}{' '}
              kg
            </span>
          );
        },
      },
      {
        field: 'period',
        headerName: t('table.period'),
        sortable: false,
        minWidth: 120,
        valueGetter: (params) => params.data?.shipment?.period ?? '',
      },
      {
        field: 'financial_status',
        headerName: t('table.financial_status'),
        sortable: false,
        minWidth: 150,
        valueGetter: (params) => params.data?.shipment?.financial_status ?? 'BLOCKED',
        cellRenderer: (params: any) => {
          const status = params.data?.shipment?.financial_status ?? 'BLOCKED';
          const statusTranslations: Record<string, string> = {
            NOT_INVOICED: t('filters.financialStatusOptions.notInvoiced', 'No facturado'),
            PREINVOICED: t('filters.financialStatusOptions.preinvoiced', 'Prefacturado'),
            INVOICED: t('filters.financialStatusOptions.invoiced', 'Facturado'),
            BLOCKED: t('filters.financialStatusOptions.blocked', 'Bloqueado'),
          };
          return <span className="text-sm">{statusTranslations[status] || status || '-'}</span>;
        },
      },
      // 🎯 Columnas específicas de bloqueados
      {
        field: 'block_reason',
        headerName: t('table.block_reason'),
        sortable: false,
        minWidth: 180,
        valueGetter: (params) => {
          // La API puede devolver 'reason' o 'shipment_block_reason'
          return params.data?.reason?.name ?? params.data?.shipment_block_reason?.name ?? '';
        },
        cellRenderer: (params: any) => {
          // La API puede devolver 'reason' o 'shipment_block_reason'
          const reason = params.data?.reason?.name ?? params.data?.shipment_block_reason?.name ?? '-';
          return <span className="text-sm">{reason}</span>;
        },
      },
      {
        field: 'block_reason_description',
        headerName: t('table.block_reason_description'),
        sortable: false,
        minWidth: 250,
        valueGetter: (params) => {
          // La API puede devolver 'reason' o 'shipment_block_reason'
          return params.data?.reason?.description ?? params.data?.shipment_block_reason?.description ?? '';
        },
        cellRenderer: (params: any) => {
          // La API puede devolver 'reason' o 'shipment_block_reason'
          const description =
            params.data?.reason?.description ?? params.data?.shipment_block_reason?.description ?? '-';
          return (
            <span className="text-sm text-muted-foreground" title={description}>
              {description.length > 60 ? `${description.substring(0, 60)}...` : description}
            </span>
          );
        },
      },
      {
        field: 'block_comment',
        headerName: t('table.block_comment'),
        sortable: false,
        minWidth: 200,
        valueGetter: (params) => {
          return params.data?.comment ?? '';
        },
        cellRenderer: (params: any) => {
          const comment = params.data?.comment ?? '-';
          return (
            <span className="text-sm text-muted-foreground" title={comment}>
              {comment.length > 50 ? `${comment.substring(0, 50)}...` : comment}
            </span>
          );
        },
      },
      {
        field: 'blocked_by',
        headerName: t('table.blocked_by'),
        sortable: false,
        minWidth: 150,
        valueGetter: (params) => {
          return params.data?.user?.name ?? params.data?.user?.email ?? '';
        },
        cellRenderer: (params: any) => {
          const userName = params.data?.user?.name ?? params.data?.user?.email ?? '-';
          return <span className="text-sm">{userName}</span>;
        },
      },
    ];

    // Agregar columna de acciones si hay permisos
    if (hasShipmentActions && onUnblockShipment) {
      baseColumns.push({
        field: 'actions',
        headerName: '',
        sortable: false,
        filter: false,
        width: 50,
        maxWidth: 50,
        minWidth: 50,
        pinned: 'right',
        lockPosition: 'right',
        suppressMovable: true,
        suppressSizeToFit: true,
        suppressAutoSize: true,
        cellRenderer: (params: any) => {
          const blockedShipment = params.data;
          if (!blockedShipment || !blockedShipment.id) {
            return null;
          }

          // Maneja la acción seleccionada en el menú contextual
          const handleDropdownAction = (action: string, blockId: number) => {
            if (action === 'unblock' && onUnblockShipment) {
              onUnblockShipment(blockId);
            }
          };

          return (
            <div className="flex justify-center w-full">
              <BlockedShipmentDropdownActions blockId={blockedShipment.id} onAction={handleDropdownAction} />
            </div>
          );
        },
      });
    }

    return baseColumns;
  }, [t, currency, hasShipmentActions, onUnblockShipment]);

  const tanStackColumns = useMemo(() => convertToTanStackColumns(allColumnDefs, t), [allColumnDefs, t]);

  const filteredColumnDefs = useMemo(() => {
    if (!selectedColumns || selectedColumns.length === 0) {
      return allColumnDefs;
    }

    return allColumnDefs.filter((col) => {
      const field = col.field || '';
      // Siempre incluir la columna de acciones si existe y hay permisos
      if (field === 'actions') {
        return hasShipmentActions && onUnblockShipment;
      }
      return selectedColumns.includes(field);
    });
  }, [allColumnDefs, selectedColumns, hasShipmentActions, onUnblockShipment]);

  return {
    allColumnDefs: filteredColumnDefs,
    filteredColumnDefs,
    tanStackColumns,
  };
};
